import React, { useState, useEffect } from "react"
import { useSelector, useDispatch } from "react-redux"
import { Row, Col } from "react-bootstrap"

import { PENDING, IDLE } from "const"
import { profileRoute } from "client/parts/routes"
import { MtUser, MtUserCreate } from "server/models"
import {
    useInput,
    StateType,
    forceRerenderContent,
} from "client/utils"
import { updateServerUserData } from "client/socketApi"
import {
    StoreType,
    UserValidation,
    UserData,

    LoadUserByLoginOrEmail,
    LoadRegisteredUserByEmail,
    SaveUser,

    closeModal,
    setUserProfileEdit,
    setUserProfileReloadView,
    setUserProfileReloadEdit,
    setSystemUserData,
} from "client/store"
import {
    PageForm,
    FormGroup,
    Label,
    Input,
    Feedback,
    FileLoader,
    Button,
    SpinnerIcon,
} from "client/components"

interface EditProfileProps {
    profile: MtUser
}

let isMounted: boolean

const Form = ({
    profile,
}: EditProfileProps) => {
    const modals = useSelector((state: StoreType) => state.system.modals)
    const userData = useSelector((state: StoreType) => state.system.userData)
    const lang = useSelector((state: StoreType) => state.system.lang)
    const dispatch = useDispatch()

    const [firstName, setFirstName] = useInput(profile.firstName)
    const [lastName, setLastName] = useInput(profile.lastName)
    const [login, setLogin] = useInput(profile.login)
    const [email, setEmail] = useInput(profile.email)

    const [logoId, setLogoId] = useState(profile.logoId)
    const [logo, setLogo] = useState(profile.logo)

    const [validation, setValidation] = useState<UserValidation>(null)

    const [saveState, setSaveState] = useState<StateType>(IDLE)
    const [applyState, setApplyState] = useState<StateType>(IDLE)

    useEffect(() => {
        isMounted = true
        return () => {
            isMounted = false
        }
    }, [])

    const onClose = () => {
        if (!isMounted) return

        if (modals.length) {
            dispatch(closeModal())
            return
        }

        forceRerenderContent(profileRoute, dispatch)
    }

    const onSave = (mode: number) => {
        if (mode === 1) setSaveState(PENDING)
        if (mode === 2) setApplyState(PENDING)

        const newProfileData: MtUserCreate = {
            firstName,
            lastName,
            login,
            email,
            logoId,
            status: profile.status,
        }

        Promise.all([
            // check if the entered email is reserved by registered user and add user-friendly messages
            LoadUserByLoginOrEmail(login.length ? login : "0"),
            LoadUserByLoginOrEmail(email.length ? email : "0"),
            LoadRegisteredUserByEmail(email.length ? email : "0"),
        ]).then(([existingUserByLogin, existingUserByEmail, registeredUser]) => {
            if (
                (existingUserByLogin && existingUserByLogin.id !== profile.id)
                || (existingUserByEmail && existingUserByEmail.id !== profile.id)
                || registeredUser
            ) {
                let userValidation: UserValidation = {}
                if (existingUserByLogin && existingUserByLogin.id !== profile.id) {
                    userValidation = {
                        ...userValidation,
                        login: lang.userWithLoginExists,
                    }
                }
                if (
                    (existingUserByEmail && existingUserByEmail.id !== profile.id)
                    || registeredUser
                ) {
                    userValidation = {
                        ...userValidation,
                        email: lang.userWithEmailExists,
                    }
                }

                setValidation(userValidation)
                setSaveState(IDLE)
                setApplyState(IDLE)
                return
            }

            SaveUser(profile.id, newProfileData, lang)
            .then((res) => {
                if (isMounted) {
                    setValidation(res.validation)
                    setSaveState(IDLE)
                    setApplyState(IDLE)
                }
                if (res.validation) return

                dispatch(setUserProfileEdit({
                    id: profile.id,
                    ...newProfileData,
                }))

                const newSystemUserData: UserData = {
                    ...userData,
                    userName: `${firstName} ${lastName}`,
                    userLogo: logo,
                }
                dispatch(setSystemUserData(newSystemUserData))
                updateServerUserData(newSystemUserData)

                dispatch(setUserProfileReloadView(false))
                setTimeout(() => {
                    dispatch(setUserProfileReloadView(true))
                }, 0)

                if (mode === 1) onClose()
                if (mode === 2) {
                    dispatch(setUserProfileReloadEdit(false))
                    setTimeout(() => {
                        dispatch(setUserProfileReloadEdit(true))
                    }, 0)
                }
            })
        })
    }

    return (
        <PageForm>
            <Row>
                <Col xs={12}>
                    <FormGroup>
                        <Label>{lang.firstName}</Label>
                        <Input type="text" value={firstName} onChange={setFirstName} />
                        <Feedback>{validation && validation.firstName}</Feedback>
                    </FormGroup>
                    <FormGroup>
                        <Label>{lang.lastName}</Label>
                        <Input type="text" value={lastName} onChange={setLastName} />
                        <Feedback>{validation && validation.lastName}</Feedback>
                    </FormGroup>
                    <FormGroup>
                        <Label>{lang.login}</Label>
                        <Input type="text" value={login} onChange={setLogin} />
                        <Feedback>{validation && validation.login}</Feedback>
                    </FormGroup>
                    <FormGroup>
                        <Label>{lang.email}</Label>
                        <Input type="text" value={email} onChange={setEmail} />
                        <Feedback>{validation && validation.email}</Feedback>
                    </FormGroup>
                    <FormGroup>
                        <Label>{lang.image}</Label>
                        <FileLoader
                            value={logo}
                            callback={(file) => {
                                setLogoId(file.id)
                                setLogo(file)
                            }}
                        />
                    </FormGroup>
                </Col>
                <Col xs={12}>
                    <Button variant="success" row onClick={() => onSave(1)}>
                        {saveState === PENDING ? <SpinnerIcon /> : lang.save}
                    </Button>
                    <Button variant="success" row onClick={() => onSave(2)}>
                        {applyState === PENDING ? <SpinnerIcon /> : lang.apply}
                    </Button>
                    <Button row onClick={onClose}>{lang.cancel}</Button>
                </Col>
            </Row>
        </PageForm>
    )
}
export default Form
